import { ButtonBase, Box } from '@mui/material'

export interface ViewerButtonProps {
  children: React.ReactNode
  /** Left icon */
  startIcon?: React.ReactNode
  /** Click handler */
  onClick?: () => void
  /** Disabled state */
  disabled?: boolean
  /** Active/highlighted state */
  isActive?: boolean
  /** Active color (default: white) */
  activeColor?: string
  /** Compact size for mobile */
  compact?: boolean
}

/**
 * Viewer Button - ビューワー画面用のアウトラインボタン
 *
 * ダーク背景上で使用する、アウトラインスタイルのボタン。
 * Live ボタンや再生モードボタンなど、ビューワーフッターで使用。
 *
 * @param children - ボタンのテキスト
 * @param startIcon - 左側アイコン
 * @param onClick - クリックハンドラー
 * @param disabled - 無効状態
 * @param isActive - アクティブ状態（ボーダーがハイライト）
 * @param activeColor - アクティブ時のボーダー色（デフォルト: white）
 * @param compact - コンパクトサイズ（モバイル用）
 *
 * @example
 * ```tsx
 * // Live ボタン
 * <ViewerButton
 *   startIcon={<FiberManualRecordIcon sx={{ color: isLive ? '#ED5432' : 'rgba(255,255,255,0.5)' }} />}
 *   isActive={isLive}
 *   activeColor="#ED5432"
 *   onClick={handleClick}
 * >
 *   Live
 * </ViewerButton>
 *
 * // 再生モードボタン
 * <ViewerButton
 *   startIcon={<TimelineIcon />}
 *   isActive={isRecording}
 *   onClick={handleClick}
 * >
 *   再生モード
 * </ViewerButton>
 * ```
 */
export default function ViewerButton ({
  children,
  startIcon,
  onClick,
  disabled = false,
  isActive = false,
  activeColor = 'white',
  compact = false
}: ViewerButtonProps): JSX.Element {
  const inactiveColor = 'rgba(255, 255, 255, 0.5)'

  return (
    <ButtonBase
      disabled={disabled}
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        px: compact ? 1 : 1.5,
        height: compact ? 24 : 28,
        borderRadius: '6px',
        border: '1px solid',
        borderColor: isActive ? activeColor : inactiveColor,
        backgroundColor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
        color: 'white',
        fontFamily: '"Noto Sans JP", sans-serif',
        fontWeight: 700,
        fontSize: compact ? 11 : 12,
        letterSpacing: compact ? '0.88px' : '0.96px',
        lineHeight: 1,
        minWidth: 'auto',
        transition: 'border-color 0.2s, background-color 0.2s',
        '&:hover': {
          borderColor: isActive ? activeColor : 'white',
          backgroundColor: 'rgba(255, 255, 255, 0.15)'
        },
        '&:disabled': {
          opacity: 0.5,
          cursor: 'not-allowed'
        }
      }}
    >
      {/* Left icon */}
      {startIcon != null && (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 0,
            '& > svg': {
              fontSize: compact ? 14 : 16
            }
          }}
        >
          {startIcon}
        </Box>
      )}

      {/* Text */}
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          lineHeight: 1.5
        }}
      >
        {children}
      </Box>
    </ButtonBase>
  )
}
